/**
 * Les avoirs d'une famille, et sa demande de remboursement.
 *
 * ## Ce que « demander » veut dire, exactement
 *
 * Le montant demandé **quitte les avoirs immédiatement**. Pas quand
 * l'administration regarde la demande, pas quand le virement part : à la
 * seconde où la famille clique.
 *
 * Ce n'est pas une précaution de principe. Sans elle, une famille pourrait
 * demander deux fois le même argent — et rien, dans le second virement, ne
 * dirait qu'il est en trop. La déduction vit dans
 * `v_user_provision_balances.available_cents`, qui soustrait les demandes
 * `en_attente` : le contrôle et l'affichage lisent donc **le même chiffre**,
 * et ne peuvent pas diverger.
 *
 * ## La demande porte sa propre communication
 *
 * Préfixe `900`, clé modulo 97. C'est ce qui distingue ce circuit de celui des
 * notes de crédit : le virement sortant portera cette communication, et se
 * rapprochera **tout seul** à la lecture du CODA suivant. Les remboursements de
 * notes, eux, sont annotés à la main et lus par expression régulière.
 *
 * Le préfixe a été choisi sur mesure, le 13 septembre 2026 : sur les 8 246
 * communications structurées reçues, les préfixes `000`, `003`, `004`,
 * `100-112`, `125`, `126`, `201-212`, `301-312`, `363`, `401-412`, `501-512`,
 * `700` et `726` sont occupés — et **aucun `9xx` ne circule**. Le `200` que
 * proposait la documentation aurait collisionné avec le parascolaire, dont le
 * seul préfixe `206` porte 344 paiements.
 */

/* `db` et non `supabase` : le client re-type sans schema. `database.types.ts`
   ne connait ni `v_user_provision_balances` ni `refund_requests`, et le
   regenerer demanderait de monter @supabase/supabase-js — une decision a
   prendre a part. Voir l'en-tete de `payments/db.ts`. C'est la MEME instance,
   donc la meme session. */
import { db } from '../payments/db';
import {
  versEtatDeVerification,
  versMode,
  type EtatDeVerification,
  type ModeAvoirAuto,
} from './avoirAuto';

export interface MesAvoirs {
  /** Ce qui a été mis en provision au total. */
  crediteCents: number;
  /** Ce qui a déjà été rendu. */
  renduCents: number;
  /** Demandé, retiré des avoirs, pas encore viré. */
  enAttenteCents: number;
  /**
   * Ce qui peut servir **maintenant** — jamais négatif.
   *
   * Depuis le 18 septembre 2026, c'est zéro tant que le compte n'a pas été
   * vérifié ou que `provision_auto` est fermé. La famille possède toujours son
   * avoir : voir `avoirCents`.
   */
  disponibleCents: number;
  /** Ce que la famille possède, réservations déduites, verrou ou non. */
  avoirCents: number;
  /** `incomplet` tant que personne n'a attesté le compte. */
  verificationEtat: EtatDeVerification;
}

/**
 * La part de l'avoir que le verrou retient.
 *
 * C'est une soustraction, pas une troisième source : afficher ce chiffre à
 * côté du disponible évite au parent de lire un zéro sans explication.
 */
export function enVerificationCents(a: MesAvoirs): number {
  return Math.max(0, a.avoirCents - a.disponibleCents);
}

export async function loadMesAvoirs(userId: string): Promise<MesAvoirs> {
  const { data, error } = await db
    .from('v_user_provision_balances')
    .select('credited_cents, refunded_cents, pending_cents, available_cents, avoir_cents, verification_etat')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;

  /* Aucune ligne veut dire aucun avoir : la vue ne garde que les familles
     concernées. Rendre des zéros plutôt que `null` évite à l'écran d'avoir
     deux façons de dire la même chose. */
  const r = (data ?? {}) as Record<string, unknown>;
  return {
    crediteCents: Number(r.credited_cents ?? 0),
    renduCents: Number(r.refunded_cents ?? 0),
    enAttenteCents: Number(r.pending_cents ?? 0),
    disponibleCents: Number(r.available_cents ?? 0),
    avoirCents: Number(r.avoir_cents ?? 0),
    verificationEtat: versEtatDeVerification(r.verification_etat),
  };
}

/* -------------------------------------------------------------------------- */
/* Le réglage de l'usage automatique                                           */
/* -------------------------------------------------------------------------- */

/**
 * Le mode en vigueur.
 *
 * Ne lève jamais : une lecture qui échoue rend `off`. Ce module ne sert qu'à
 * expliquer et à régler ; le verrou lui-même tient en base, et il tiendrait
 * même si cet écran mentait.
 */
export async function lireProvisionAuto(): Promise<ModeAvoirAuto> {
  const { data, error } = await db
    .from('site_reglages')
    .select('valeur')
    .eq('cle', 'provision_auto')
    .maybeSingle();
  if (error) {
    console.error('Réglage provision_auto non lu, blocage suppose :', error);
    return 'off';
  }
  return versMode((data as { valeur?: unknown } | null)?.valeur);
}

/** Change le mode. Réservé aux administrateurs par la RLS de `site_reglages`. */
export async function ecrireProvisionAuto(mode: ModeAvoirAuto, auteurId: string | null): Promise<void> {
  const { error } = await db.from('site_reglages').upsert(
    { cle: 'provision_auto', valeur: mode, maj_le: new Date().toISOString(), maj_par: auteurId },
    { onConflict: 'cle' },
  );
  if (error) throw new Error(error.message);
}

export type StatutDeDemande = 'en_attente' | 'payee' | 'refusee' | 'annulee';

export interface MaDemande {
  id: string;
  createdAt: string;
  amountCents: number;
  status: StatutDeDemande;
  /** La forme lisible `+++900/0000/00123+++`, à recopier sur un virement. */
  communication: string;
  motif: string | null;
}

/** Les 12 chiffres en `+++123/4567/89012+++`. */
function formatStructuree(digits: string): string {
  return /^\d{12}$/.test(digits)
    ? `+++${digits.slice(0, 3)}/${digits.slice(3, 7)}/${digits.slice(7)}+++`
    : digits;
}

export async function loadMesDemandes(userId: string): Promise<MaDemande[]> {
  const { data, error } = await db
    .from('refund_requests')
    .select('id, created_at, amount_cents, status, communication_digits, motif')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;

  return ((data ?? []) as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r.id),
    createdAt: String(r.created_at),
    amountCents: Number(r.amount_cents),
    status: r.status as StatutDeDemande,
    communication: formatStructuree(String(r.communication_digits)),
    motif: (r.motif as string) ?? null,
  }));
}

export interface DemandeCreee {
  id: string;
  amountCents: number;
  communication: string;
}

/**
 * Demande le remboursement de tout ou partie de la provision.
 *
 * Le montant est **libre** dans la limite du disponible : une famille qui a
 * 50,00 € peut en demander 30,00 € et laisser le reste réduire sa prochaine
 * facture. C'est le geste que l'administration a décrit, et il n'y a aucune
 * raison de l'obliger à tout reprendre.
 *
 * Le contrôle du plafond se fait **en base**, dans `demander_remboursement` :
 * le vérifier ici aussi ne ferait que dupliquer une règle qui doit tenir même
 * si l'écran se trompe.
 */
export async function demanderRemboursement(
  montantCents: number,
  options: { iban?: string; titulaire?: string } = {},
): Promise<DemandeCreee> {
  const { data, error } = await db.rpc('demander_remboursement', {
    p_amount_cents: montantCents,
    p_iban: options.iban ?? null,
    p_account_holder: options.titulaire ?? null,
  });
  if (error) throw error;

  const r = (data ?? {}) as Record<string, unknown>;
  return {
    id: String(r.id),
    amountCents: Number(r.amount_cents),
    communication: String(r.communication ?? ''),
  };
}

/** Annule une demande encore en attente : le montant revient aux avoirs. */
export async function annulerMaDemande(id: string): Promise<void> {
  const { error } = await db.rpc('decider_demande_de_remboursement', {
    p_id: id,
    p_status: 'annulee',
    p_motif: null,
  });
  if (error) throw error;
}
